import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Check, X, MessageCircle, Loader2, Users, BookOpen, MapPin, User } from 'lucide-react';
import { CONNECTIONS } from '@/constants/testIds';
import { toast } from 'sonner';
import axios from 'axios';
import { VIBE_META } from '@/components/VibeQuiz';

// Small helper — resolves the profile picture URL (backend-provided path or absolute)
const buildPicUrl = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http')) return pic;
  return `${process.env.REACT_APP_BACKEND_URL}${pic.startsWith('/') ? '' : '/'}${pic}`;
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const Avatar = ({ user, size = 72 }) => {
  const url = buildPicUrl(user.profile_picture);
  return (
    <div
      className="relative flex-shrink-0 rounded-full overflow-hidden border-2 border-[#E2E6D5] shadow-md bg-gradient-to-br from-[#E2E6D5] to-[#E8EBDD] flex items-center justify-center"
      style={{ width: size, height: size }}
      data-testid={`avatar-${user._id || 'anon'}`}
    >
      {url ? (
        <img
          src={url}
          alt={user.name || 'user'}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <span
          className="font-serif font-bold text-[#6B7A4F]"
          style={{ fontSize: size * 0.4 }}
        >
          {initials(user.name)}
        </span>
      )}
    </div>
  );
};

const VibeBadge = ({ vibeKey }) => {
  const meta = VIBE_META && VIBE_META[vibeKey];
  if (!meta) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded-full"
      style={{
        background: `${meta.color}20`,
        color: meta.color,
        border: `1px solid ${meta.color}55`,
      }}
    >
      <span>{meta.emoji}</span>
      {meta.name}
    </span>
  );
};

const ConnectionCard = ({ user, onMessage }) => (
  <div
    data-testid={CONNECTIONS.connectedUserCard}
    className="glass-card p-5 rounded-2xl hover:shadow-lg transition-shadow"
  >
    <div className="flex items-start gap-4 mb-3">
      <Avatar user={user} size={80} />
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-xl text-[#2D2A26] leading-tight truncate">
          {user.name}
        </h3>
        <div className="flex flex-wrap items-center gap-2 mt-1 mb-2">
          {user.city && (
            <span className="inline-flex items-center gap-1 text-xs text-[#5C5955]">
              <MapPin className="w-3 h-3" /> {user.city}
            </span>
          )}
          {typeof user.book_count === 'number' && (
            <span className="inline-flex items-center gap-1 text-xs text-[#5C5955]">
              <BookOpen className="w-3 h-3" />
              {user.book_count} {user.book_count === 1 ? 'book' : 'books'}
            </span>
          )}
        </div>
        {user.reader_vibe && <VibeBadge vibeKey={user.reader_vibe} />}
      </div>
    </div>

    {user.bio && (
      <p className="text-sm text-[#5C5955] mb-3 italic leading-snug">
        "{user.bio}"
      </p>
    )}

    {user.favorite_genres && user.favorite_genres.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-4">
        {user.favorite_genres.slice(0, 5).map((genre) => (
          <span key={genre} className="genre-badge bg-[#E2E6D5] text-[#6B7A4F]">{genre}</span>
        ))}
      </div>
    )}

    <Button
      data-testid={CONNECTIONS.messageBtn}
      onClick={onMessage}
      className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
    >
      <MessageCircle className="w-4 h-4 mr-2" />
      Send Message
    </Button>
  </div>
);

const RequestCard = ({ request, onAccept, onReject }) => {
  const r = request.requester || {};
  return (
    <div
      data-testid={CONNECTIONS.requestCard}
      className="glass-card p-5 rounded-2xl"
    >
      <div className="flex items-start gap-4 mb-3">
        <Avatar user={r} size={72} />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-xl text-[#2D2A26] leading-tight truncate">
            {r.name || 'A new reader'}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-1 mb-2">
            {r.city && (
              <span className="inline-flex items-center gap-1 text-xs text-[#5C5955]">
                <MapPin className="w-3 h-3" /> {r.city}
              </span>
            )}
            {typeof r.book_count === 'number' && (
              <span className="inline-flex items-center gap-1 text-xs text-[#5C5955]">
                <BookOpen className="w-3 h-3" />
                {r.book_count} {r.book_count === 1 ? 'book' : 'books'}
              </span>
            )}
          </div>
          {r.reader_vibe && <VibeBadge vibeKey={r.reader_vibe} />}
        </div>
      </div>

      {r.bio && (
        <p className="text-sm text-[#5C5955] mb-3 italic leading-snug">
          "{r.bio}"
        </p>
      )}

      {request.message && (
        <p className="text-sm text-[#2D2A26] bg-[#E2E6D5]/30 border-l-4 border-[#6B7A4F] p-3 rounded-r-lg mb-3">
          "{request.message}"
        </p>
      )}

      {r.favorite_genres && r.favorite_genres.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {r.favorite_genres.slice(0, 5).map((genre) => (
            <span key={genre} className="genre-badge bg-[#E2E6D5] text-[#6B7A4F]">{genre}</span>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          data-testid={CONNECTIONS.acceptBtn}
          onClick={onAccept}
          className="flex-1 bg-[#8E9E82] hover:bg-[#7A8A72] text-white rounded-full"
        >
          <Check className="w-4 h-4 mr-2" /> Accept
        </Button>
        <Button
          data-testid={CONNECTIONS.rejectBtn}
          onClick={onReject}
          variant="outline"
          className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full"
        >
          <X className="w-4 h-4 mr-2" /> Decline
        </Button>
      </div>
    </div>
  );
};

const Connections = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, connRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/connections/requests`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/connections`, { withCredentials: true })
      ]);
      setRequests(reqRes.data);
      setConnections(connRes.data);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success('Connection accepted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept connection');
    }
  };

  const handleReject = async (connectionId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success('Connection declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject connection');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-4 text-[#5C5955] hover:text-[#6B7A4F]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div data-testid={CONNECTIONS.container} className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-6 sm:mb-8">
            Your <span className="font-bold">Book Besties</span>
          </h1>

          <Tabs defaultValue="connections" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 sm:mb-8">
              <TabsTrigger value="connections">
                Connections ({connections.length})
              </TabsTrigger>
              <TabsTrigger value="requests">
                Pending ({requests.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="connections">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" />
                </div>
              ) : connections.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-2xl">
                  <Users className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">No besties yet</h3>
                  <p className="text-[#5C5955] mb-4">Start connecting with fellow book lovers</p>
                  <Button
                    onClick={() => navigate('/discover')}
                    className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                  >
                    Discover Matches
                  </Button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-5">
                  {connections.map((connection) => (
                    <ConnectionCard
                      key={connection._id}
                      user={connection}
                      onView={() => navigate(`/users/${connection._id}`)}
                      onMessage={() => navigate(`/messages?user=${connection._id}`)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="requests">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-2xl">
                  <Users className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">No pending requests</h3>
                  <p className="text-[#5C5955]">New connection requests will show up here</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-5">
                  {requests.map((request) => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      onAccept={() => handleAccept(request.id)}
                      onReject={() => handleReject(request.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Connections;
